/**
 * UI/UX preferences 型 (Track D)。
 *
 * backend DTO (`apps/api/src/preferences/dto/preferences.dto.ts`) と 1:1 同期。
 * 全フィールド optional — undefined はクライアント既定値で動作する。
 *
 * セキュリティ:
 *   - 非機密 (CLAUDE.md §2.6 の禁止対象外)。サーバ JSONB / IndexedDB / メモリの
 *     3 段キャッシュで複数デバイス間同期する
 */

export const THEME_VALUES = ['system', 'light', 'dark'] as const
export type ThemePreference = (typeof THEME_VALUES)[number]

export const LANGUAGE_VALUES = ['ja', 'en'] as const
export type LanguagePreference = (typeof LANGUAGE_VALUES)[number]

/** アバター背景色パレット (画像アップロードの代替、非機密)。 */
export const AVATAR_COLOR_VALUES = [
  'slate',
  'red',
  'orange',
  'amber',
  'green',
  'teal',
  'blue',
  'violet',
] as const
export type AvatarColorPreference = (typeof AVATAR_COLOR_VALUES)[number]

export interface UserPreferences {
  theme?: ThemePreference
  language?: LanguagePreference
  /** アイドルロックまでの ms ([60_000, 3_600_000]、ookey-ios と同期した 1/5/15/30/60 分の 5 値)。
   * 特殊値 `0` は「ブラウザを閉じたときのみロック」(idle timer 無効)。
   * web は sessionStore が in-memory のためタブクローズで自然にロックされる。 */
  idleLockMs?: number
  /** クリップボード自動クリア秒数 ([5, 600]、既定 90 = CLAUDE.md §2 ルール 7) */
  clipboardTtlSec?: number
  /** お気に入りを vault list 上部に固定 */
  favoritesOnTop?: boolean
  /** 同等ドメインを vault list で統合表示 */
  mergeDomains?: boolean
  /** アバター背景色 (画像アップロード非対応、色のみカスタマイズ) */
  avatarColor?: AvatarColorPreference
  /** ブラウザ拡張のオートフィルを有効にするか (web app は値の保持のみ、拡張が参照) */
  autofill?: boolean
  /** 通知を有効にするか */
  notifications?: boolean
}

/**
 * クライアント既定値。サーバが未設定 (`{}`) を返した場合と、IndexedDB / network
 * が利用不能な offline 時に使う。
 */
export const DEFAULT_PREFERENCES: Required<UserPreferences> = {
  // T-M post-Phase 6: 既定 theme は light (marketing landing が light を
  // 想定したデザインで、auth/vault 画面も light 既定でブランド一貫)。
  // user が settings から dark / system に切替可能。
  theme: 'light',
  language: 'ja',
  idleLockMs: 30 * 60 * 1000, // 30 分 (UX 重視の既定値、設定可: 1/5/15/30/60 分 ookey-ios と統一)
  clipboardTtlSec: 90, // CLAUDE.md §2 ルール 7
  favoritesOnTop: true,
  mergeDomains: true,
  avatarColor: 'slate',
  autofill: true,
  notifications: false,
} as const

/**
 * Merge 後の resolved preferences。UI が常に値を持つことを型レベルで保証する
 * ため `Required` で穴を埋めた snapshot。`mergePreferences()` の戻り値型。
 */
export type ResolvedPreferences = Required<UserPreferences>

export const mergePreferences = (partial: UserPreferences | null): ResolvedPreferences => ({
  ...DEFAULT_PREFERENCES,
  ...(partial ?? {}),
})
